import { Fragment, KeyboardEvent } from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import { COLOURS } from '@belong/themes';
import {
  ColoursValue,
  ICON_NAMES,
  IGlobalHeaderUrls,
  IHeaderMenu,
  IOrganismMenuItem,
  IRichTextProps
} from '@belong/types';
import { Column, Columns, ContentWrapper, RichText, Stack } from '@belong/ui-components';
import { removeTrailingSlash } from '@belong/url-utils';
import { logout } from '@belong/user';
import { GLOBAL_HEADER_TEST_ID as TEST_ID } from '../../testids';
import { ARIA_CURRENT } from '../../variables';
import { THeaderDropdownWrapper } from '../types';
import { DropdownHeading } from './DropdownHeading';
import { DropdownLink } from './DropdownLink';
import DropdownLinkGroup from './DropdownLinkGroup';
import * as styles from './styles';

interface IHeaderDropdown {
  headerMenuContent: IHeaderMenu;
  dropdownMenuContent?: IOrganismMenuItem;
  currentUrl?: string;
  dropdownWrapper: THeaderDropdownWrapper;
  hideMyAccountLinks?: boolean;
  urls: IGlobalHeaderUrls;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  onLinkClick?: () => void;
}

const renderSummaryParagraphNode = (color: ColoursValue): IRichTextProps['renderNode'] => ({
  [BLOCKS.PARAGRAPH]: (_, children) => <styles.SummaryCopy hasColor={color}>{children}</styles.SummaryCopy>
});

export const HeaderDropdown = ({
  headerMenuContent,
  dropdownMenuContent,
  currentUrl,
  dropdownWrapper,
  hideMyAccountLinks,
  urls,
  onBlur,
  onKeyDown,
  onLinkClick
}: IHeaderDropdown) => {
  return (
    <>
      <styles.StyledContainer isPadded={false}>
        {dropdownMenuContent && (
          <ContentWrapper showContent={dropdownWrapper === 'content-main'} data-testid={TEST_ID.DROPDOWN_CONTENT}>
            <Columns space={{ xs: 'large', md: 'xlarge' }} alignX="left">
              {dropdownMenuContent?.summary && (
                <Column width="1/4">
                  <styles.SummaryContainer data-testid={TEST_ID.DROPDOWN_SUMMARY}>
                    <Stack space="small">
                      <RichText
                        {...dropdownMenuContent.summary}
                        hasColor={COLOURS.WHITE}
                        h2="headingMedium"
                        renderNode={renderSummaryParagraphNode(COLOURS.GREY_100)}
                      />
                      {dropdownMenuContent.summaryLink && (
                        <DropdownLink
                          href={dropdownMenuContent.summaryLink.href}
                          iconName={dropdownMenuContent.summaryLink.navLinkIcon}
                          label={dropdownMenuContent.summaryLink.label}
                          onClick={onLinkClick}
                          onKeyDown={onKeyDown}
                          aria-current={
                            currentUrl === removeTrailingSlash(dropdownMenuContent.summaryLink.href)
                              ? ARIA_CURRENT
                              : undefined
                          }
                        />
                      )}
                    </Stack>
                  </styles.SummaryContainer>
                </Column>
              )}

              {/* Column 1 of the navLinkGroups */}
              {dropdownMenuContent?.navGroup1 && (
                <Column width="1/2">
                  <styles.MiddleColumnWrapper
                    rows={
                      dropdownMenuContent.navGroup1?.length <= 2
                        ? 1
                        : Math.ceil(dropdownMenuContent.navGroup1?.length / 2)
                    }
                  >
                    {dropdownMenuContent?.navGroup1?.map((navLinkGroup, idx: number) => (
                      <Column width="1" key={navLinkGroup.id}>
                        <DropdownHeading headingColor={navLinkGroup.labelColour}>{navLinkGroup.label}</DropdownHeading>
                        {navLinkGroup.additionalHelpText && <styles.HelpText {...navLinkGroup.additionalHelpText} />}
                        <DropdownLinkGroup
                          navLinkGroup={navLinkGroup}
                          currentUrl={currentUrl}
                          onLinkClick={onLinkClick}
                          onKeyDown={onKeyDown}
                          onBlur={
                            !dropdownMenuContent.navGroup2 &&
                            dropdownMenuContent.navGroup1 &&
                            dropdownMenuContent.navGroup1.length - 1 === idx
                              ? onBlur
                              : undefined
                          }
                        />
                      </Column>
                    ))}
                  </styles.MiddleColumnWrapper>
                </Column>
              )}
              {/* Column 2 of the navLinkGroups */}
              {dropdownMenuContent.navGroup2 && (
                <Column width="1/4">
                  <Stack>
                    {/* the Stack is cover in case someone decides they wanna add more to this column */}
                    {dropdownMenuContent.navGroup2.map((navLinkGroup, idx: number) => (
                      <Fragment key={navLinkGroup.id}>
                        <DropdownHeading headingColor={navLinkGroup.labelColour}>{navLinkGroup.label}</DropdownHeading>
                        {navLinkGroup.additionalHelpText && <styles.HelpText {...navLinkGroup.additionalHelpText} />}
                        <DropdownLinkGroup
                          navLinkGroup={navLinkGroup}
                          currentUrl={currentUrl}
                          onLinkClick={onLinkClick}
                          onKeyDown={onKeyDown}
                          onBlur={
                            dropdownMenuContent.navGroup2 && dropdownMenuContent.navGroup2.length - 1 === idx
                              ? onBlur
                              : undefined
                          }
                        />
                      </Fragment>
                    ))}
                  </Stack>
                </Column>
              )}
            </Columns>
          </ContentWrapper>
        )}
        {headerMenuContent && (
          <ContentWrapper showContent={dropdownWrapper === 'content-account'}>
            <styles.ProfileMenuFlex>
              <styles.ProfileMenuColumn>
                <styles.MenuList>
                  {headerMenuContent.myServicesLabel && !hideMyAccountLinks && (
                    <li>
                      <DropdownLink
                        aria-current={currentUrl === urls.myServices ? ARIA_CURRENT : undefined}
                        data-testid={TEST_ID.BUTTON_MY_SERVICES}
                        href={urls.myServices}
                        label={headerMenuContent.myServicesLabel}
                        iconName={ICON_NAMES.MyServices}
                        onClick={onLinkClick}
                      />
                    </li>
                  )}
                  {headerMenuContent.myProfileLabel && !hideMyAccountLinks && (
                    <li>
                      <DropdownLink
                        aria-current={currentUrl === urls.myProfile ? ARIA_CURRENT : undefined}
                        data-testid={TEST_ID.BUTTON_MY_PROFILE}
                        href={urls.myProfile}
                        label={headerMenuContent.myProfileLabel}
                        iconName={ICON_NAMES.Profile}
                        onClick={onLinkClick}
                        csr
                      />
                    </li>
                  )}
                  {headerMenuContent.logoutLabel && (
                    <li>
                      <styles.LogoutButton
                        label={headerMenuContent.logoutLabel}
                        data-testid={TEST_ID.BUTTON_LOGOUT}
                        icon={headerMenuContent.logoutIcon?.icon}
                        onClick={() => logout('/')}
                        variant="primary"
                        onKeyDown={onKeyDown}
                        onBlur={onBlur}
                        hasIconLeft
                        isLightColor
                      />
                    </li>
                  )}
                </styles.MenuList>
              </styles.ProfileMenuColumn>
            </styles.ProfileMenuFlex>
          </ContentWrapper>
        )}
      </styles.StyledContainer>
      <styles.AccentStrip />
    </>
  );
};

HeaderDropdown.displayName = 'HeaderDropdown';
